/**
 * Order of the Mana Pricing results (user rule 2026-09-14): every option is shown at its best
 * rate (★). Within a term × buydown group the lowest ★ rate comes first; options at the same ★
 * rate — the same lender's other program or another lender — rank by the higher net lender
 * credit. Options with no ★ (no rate meets the MLO's comp + closing criteria) come last, shown
 * at the rate priced closest to the desired price.
 */

export interface RankCriteria {
  comp: number;
  closing: number;
}

/**
 * Lender credit left after what ★ has to cover: the closing costs, a seller/borrower-paid buydown
 * escrow and — when the comp is borrower-paid — the comp. The credit itself is already net of a
 * lender-paid comp. Without criteria only the escrow is taken off.
 */
export function netLenderCredit(
  point: { discountDollars: number; buydownCost: number | null },
  criteria: RankCriteria | null,
  borrowerPaidComp: boolean,
): number {
  const closing = criteria?.closing ?? 0;
  const comp = criteria && borrowerPaidComp ? criteria.comp : 0;
  return point.discountDollars - (point.buydownCost ?? 0) - closing - comp;
}

export interface RankedOption {
  starred: boolean;
  rate: number;
  netCredit: number;
  lender: string;
  product: string;
}

/** ★ options first, lowest rate first, same rate → higher net lender credit (then a stable name order). */
export function bestRateOrder(a: RankedOption, b: RankedOption, rateDirection: 1 | -1 = 1): number {
  return (
    Number(b.starred) - Number(a.starred) ||
    (a.rate - b.rate) * rateDirection ||
    b.netCredit - a.netCredit ||
    a.lender.localeCompare(b.lender) ||
    a.product.localeCompare(b.product)
  );
}
